import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ChartLine, RefreshCw, TrendingUp, TrendingDown, ShoppingCart, Download } from "lucide-react";

function MetricCard({ label, value, subtitle, trend, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-slate-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? "text-green-600" : "text-red-600"}`}>
          {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {trend >= 0 ? "+" : ""}{trend.toFixed(1)}% vs periodo anterior
        </div>
      )}
    </div>
  );
}

export default function OpsAnalisis() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState("");

  const today = new Date();
  const thirtyDaysAgo = new Date(today); thirtyDaysAgo.setDate(today.getDate() - 30);
  const sixtyDaysAgo = new Date(today); sixtyDaysAgo.setDate(today.getDate() - 60);

  const fmt = d => d.toISOString().split("T")[0];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        from_date: fmt(thirtyDaysAgo), to_date: fmt(today), period: "daily",
      });
      if (filterBranch) params.set("branch_id", filterBranch);

      const params2 = new URLSearchParams({
        period1_start: fmt(thirtyDaysAgo), period1_end: fmt(today),
        period2_start: fmt(sixtyDaysAgo), period2_end: fmt(thirtyDaysAgo),
      });
      if (filterBranch) { params2.set("branch_id", filterBranch); }

      const [sumRes, compRes, brRes] = await Promise.all([
        api.get(`/ops/sales/summary?${params}`),
        api.get(`/ops/sales/comparison?${params2}`),
        api.get("/branches"),
      ]);
      setSummary(sumRes.data);
      setComparison(compRes.data);
      setBranches(brRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterBranch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post("/ops/wansoft/sync-sales");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await api.get("/ops/wansoft/sync-runs/last");
          const run = r.data?.run;
          if (run && run.estado === "corriendo" && tries < 40) {
            setTimeout(poll, 5000);
          } else {
            setSyncing(false);
            fetchData();
          }
        } catch {
          setSyncing(false);
          fetchData();
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      console.error(e);
      setSyncing(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ from_date: fmt(thirtyDaysAgo), to_date: fmt(today) });
      if (filterBranch) params.set("branch_id", filterBranch);
      const resp = await api.get(`/ops/sales/export.xlsx?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "ventas.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const totals = summary?.totals || {};
  const diffPct = comparison?.diff_pct || 0;

  const dailyData = (summary?.data || []).slice(0, 30);
  const maxRevenue = Math.max(...dailyData.map(d => d.total || 0), 1);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ops-analisis-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ChartLine className="w-6 h-6 text-yellow-500" /> Analisis de ventas
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Ultimos 30 dias — fuente:{" "}
            <span data-testid="ventas-source-badge" className="font-medium text-slate-700">
              {summary?.data_source === "wansoft"
                ? "POS Wansoft"
                : summary?.data_source === "corte"
                  ? "cortes declarados"
                  : summary?.data_source === "mixed"
                    ? `mixta (${summary?.sources?.wansoft || 0} dias POS + ${summary?.sources?.corte || 0} dias corte)`
                    : "sin datos"}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <select value={filterBranch} onChange={e => setFilterBranch(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
            <option value="">Todas las sucursales</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {user?.role === "corporate_admin" && (
            <button onClick={handleSync} disabled={syncing} data-testid="btn-sync-wansoft"
              className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando..." : "Sync Wansoft"}
            </button>
          )}
          <button onClick={handleExport} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
            <Download className="w-4 h-4" /> Exportar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              label="Total ordenes" value={(totals.total_orders || 0).toLocaleString("es-MX")}
              subtitle={`${totals.days_with_data || 0} dias con datos`} icon={ShoppingCart}
            />
            <MetricCard
              label="Ticket promedio" value={`$${(totals.avg_ticket || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`}
              icon={TrendingUp}
            />
            <MetricCard
              label="Ingresos totales" value={`$${(totals.total_revenue || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`}
              trend={diffPct} icon={ChartLine}
            />
            <MetricCard
              label="Vs periodo anterior" value={`${diffPct >= 0 ? "+" : ""}${diffPct.toFixed(1)}%`}
              subtitle={`$${(comparison?.diff_revenue || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`}
            />
          </div>

          {/* Mini chart — CSS bars */}
          {dailyData.length > 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Ventas diarias (ultimos 30 dias)</h3>
              <div className="flex items-end gap-0.5 h-32">
                {dailyData.map((d, i) => {
                  const height = Math.max(((d.total || 0) / maxRevenue) * 100, 1);
                  return (
                    <div key={i} className="flex-1 group relative">
                      <div
                        className="bg-[#FFDF00] hover:bg-yellow-400 transition-colors rounded-sm cursor-pointer"
                        style={{ height: `${height}%` }}
                        title={`${d.date}: $${(d.total || 0).toLocaleString("es-MX")}`}
                      ></div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>{dailyData[0]?.date}</span>
                <span>{dailyData[dailyData.length - 1]?.date}</span>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <ChartLine className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-600">Sin datos de ventas todavia</p>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                Usa "Sync Wansoft" para cargar datos historicos, o aguarda el job nocturno de sincronizacion automatica.
              </p>
            </div>
          )}

          {/* Comparison table */}
          {comparison && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Comparacion de periodos</h3>
              <div className="grid grid-cols-3 gap-4">
                {[
                  ["Periodo actual (30 dias)", comparison.period1],
                  ["Periodo anterior (30 dias)", comparison.period2],
                  ["Variacion", { total_revenue: comparison.diff_revenue, total_orders: (comparison.period1?.total_orders || 0) - (comparison.period2?.total_orders || 0), diff: true }],
                ].map(([label, data], i) => (
                  <div key={i} className={`p-4 rounded-lg ${i === 2 ? (comparison.diff_revenue >= 0 ? "bg-green-50" : "bg-red-50") : "bg-slate-50"}`}>
                    <div className="text-xs text-slate-500 mb-2">{label}</div>
                    <div className={`text-xl font-bold ${i === 2 && comparison.diff_revenue < 0 ? "text-red-700" : i === 2 ? "text-green-700" : "text-slate-900"}`}>
                      {i === 2 && comparison.diff_revenue >= 0 ? "+" : ""}
                      ${(data?.total_revenue || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}
                    </div>
                    <div className="text-xs text-slate-500">{(data?.total_orders || 0).toLocaleString("es-MX")} ordenes</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
